import { Box, Typography, Button, Container, Grid, Paper, Stack, Chip } from "@mui/material";
import { Helmet } from "react-helmet";
import { useContext } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useTheme, alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ShareIcon from "@mui/icons-material/Share";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TranslateIcon from "@mui/icons-material/Translate";

export default function LandingDesktop() {
  const { translate } = useContext(LanguageContext);
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>{translate("HeroTitleFull")}</title>
        <meta name="description" content={translate("HeroMetaDescription")} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={translate("HeroTitleFull")} />
        <meta
          property="og:description"
          content={translate("HeroMetaDescription")}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Talkmate" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={translate("HeroTitleFull")} />
        <meta
          property="twitter:description"
          content={translate("HeroMetaDescription")}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is Talkmate?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Talkmate lets you create AI agents with your own context and deploy them via embed code or shareable link.",
                },
              },
              {
                "@type": "Question",
                name: "How do I embed a Talkmate agent?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Generate the embed script or iframe from the admin, then paste it into your site to launch the chat widget.",
                },
              },
              {
                "@type": "Question",
                name: "Is there a free plan?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes, you can create an agent and share it via link or embed on the free plan, then upgrade for advanced limits.",
                },
              },
              {
                "@type": "Question",
                name: "Can I hand off to a human operator?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Talkmate supports manual replies: pause the bot and let an operator respond to the conversation.",
                },
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Talkmate",
            applicationCategory: "CustomerSupportApplication",
            operatingSystem: "Web",
            description:
              "Talkmate lets you create AI agents that answer customers automatically on your site, widget, or share link.",
            url:
              typeof window !== "undefined" && window.location
                ? window.location.origin
                : "https://talk-mate-backend.web.app",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
              description: "Free plan available; premium with advanced limits.",
            },
          })}
        </script>
      </Helmet>

      <Box
        sx={{
          position: "relative",
          color: "text.primary",
          py: { xs: 8, md: 14 },
          px: { xs: 2, md: 0 },
          background: `radial-gradient(1000px 400px at -10% -10%, ${theme.palette.mode === "dark" ? "rgba(124,77,255,0.20)" : "rgba(124,77,255,0.15)"} 0%, rgba(124,77,255,0) 60%),
                       radial-gradient(800px 300px at 110% -20%, ${theme.palette.mode === "dark" ? "rgba(0,184,255,0.20)" : "rgba(0,184,255,0.15)"} 0%, rgba(0,184,255,0) 60%),
                       linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.default} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" fontWeight={800} color="text.primary" sx={{ mb: 1.5, lineHeight: 1.1 }}>
                {translate("Landing_HeroTitle")}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2.5, fontWeight: 500 }}>
                {translate("Landing_HeroSubtitle1")}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {translate("Landing_HeroSubtitle2")}
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1, alignItems: "center" }}>
                <Button component={Link} to="/app" variant="contained" size="large" sx={{ textTransform: "none" }}>
                  {translate("Landing_CTAPrimary")}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {translate("Landing_FreeTrialLine")}
                </Typography>
              </Stack>
              {/* Premium line intentionally removed as requested */}

              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
                <Chip size="small" icon={<AccessTimeIcon />} label="24/7" variant="outlined" />
                <Chip size="small" icon={<TranslateIcon />} label={translate("Multilingual") || "Multilingue"} variant="outlined" />
                <Chip size="small" icon={<VerifiedIcon />} label="GPT-4o" variant="outlined" />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ display: 'block', mb: 2, fontWeight: 800, px: 1.5, py: 1, borderRadius: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.08), border: (t) => `1px solid ${alpha(t.palette.success.main, 0.35)}` }}>
                {translate("Landing_ExamplesHeading")}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'stretch' }}>
                <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, flex: 1 }} elevation={0}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.main }}>
                      <RestaurantMenuIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{translate("Landing_RestaurantTitle")}</Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    <Box sx={{ alignSelf: "flex-end", maxWidth: "85%", p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.18), color: theme.palette.text.primary }}>
                      <Typography variant="body2">{translate("Landing_RestaurantMsg1Client")}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-start", maxWidth: "90%", p: 1.5, borderRadius: 2, bgcolor: theme.palette.mode === "dark" ? "#1f2937" : "#f2f6ff", borderLeft: `3px solid ${theme.palette.success.main}`, border: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="body2">{translate("Landing_RestaurantMsg2Agent")}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-end", maxWidth: "85%", p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.18) }}>
                      <Typography variant="body2">{translate("Landing_RestaurantMsg3Client")}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-start", maxWidth: "90%", p: 1.5, borderRadius: 2, bgcolor: theme.palette.mode === "dark" ? "#1f2937" : "#f2f6ff", borderLeft: `3px solid ${theme.palette.success.main}`, border: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="body2">{translate("Landing_RestaurantMsg4Agent")}</Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, flex: 1 }} elevation={0}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.main }}>
                      <HomeWorkIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{translate("Landing_RealEstateTitle")}</Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    <Box sx={{ alignSelf: "flex-end", maxWidth: "85%", p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.18) }}>
                      <Typography variant="body2">{translate("Landing_RealEstateMsg1Client")}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-start", maxWidth: "90%", p: 1.5, borderRadius: 2, bgcolor: theme.palette.mode === "dark" ? "#1f2937" : "#f2f6ff", borderLeft: `3px solid ${theme.palette.success.main}`, border: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="body2">{translate("Landing_RealEstateMsg2Agent")}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-end", maxWidth: "85%", p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.18) }}>
                      <Typography variant="body2">{translate("Landing_RealEstateMsg3Client")}</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-start", maxWidth: "90%", p: 1.5, borderRadius: 2, bgcolor: theme.palette.mode === "dark" ? "#1f2937" : "#f2f6ff", borderLeft: `3px solid ${theme.palette.success.main}`, border: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="body2">{translate("Landing_RealEstateMsg4Agent")}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ px: { xs: 2, md: 0 } }}>
        <Container maxWidth="lg">
          <Typography variant="h6" sx={{ display: 'block', mb: 3, fontWeight: 800, px: 1.5, py: 1.2, textAlign: 'center', borderRadius: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.08), border: (t) => `1px solid ${alpha(t.palette.success.main, 0.35)}` }}>
            {translate("Landing_BenefitsBanner")}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: 2, px: { xs: 2, md: 0 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }} elevation={0}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                  {translate("Landing_ValueCard1Title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate("Landing_ValueCard1Desc")}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }} elevation={0}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                  {translate("Landing_ValueCard2Title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate("Landing_ValueCard2Desc")}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px dashed ${alpha(t.palette.text.primary, 0.2)}`, minHeight: 108 }} elevation={0}>
                {/* Placeholder for future content */}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, px: { xs: 2, md: 0 } }}>
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight={700} sx={{ textAlign: "center", mb: 4 }}>
            {translate("Landing_HowTitle")}
          </Typography>
          <Grid container spacing={3}>
            {[
              { icon: <EditNoteIcon color="primary" />, title: translate("Landing_HowStep1Title"), desc: translate("Landing_HowStep1Desc") },
              { icon: <AutoAwesomeIcon color="primary" />, title: translate("Landing_HowStep2Title"), desc: translate("Landing_HowStep2Desc") },
              { icon: <ShareIcon color="primary" />, title: translate("Landing_HowStep3Title"), desc: translate("Landing_HowStep3Desc") },
            ].map((step, i) => (
              <Grid key={i} item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }} elevation={0}>
                  <Stack spacing={1}>
                    <Box sx={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 1.5, bgcolor: theme.palette.primary.main + "14" }}>
                      {step.icon}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700}>{step.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
